<template>
  <div class="catalog-purchase">
    <div class="d-flex align-center flex-wrap ga-2 mb-3">
      <v-chip :disabled="disabled" @click="recipientDialog = true">
        Shopping for: {{ recipientName }}
      </v-chip>
      <v-btn
        v-if="recipient && recipient.personId !== buyer?.personId"
        :disabled="disabled"
        variant="text"
        @click="recipient = null"
        >Use buyer</v-btn
      >
    </div>
    <div class="shop-tool-panel">
      <div>
        <v-alert v-if="error && !selected" type="error" class="my-3">{{
          error
        }}</v-alert>
        <v-text-field
          v-model="search"
          v-no-autofill
          label="Search name or SKU"
          clearable
        />
        <v-tabs v-model="tab" color="primary" density="compact" class="mb-4">
          <v-tab value="all">All</v-tab>
          <v-tab
            v-for="type in typeTabs"
            :key="type.value"
            :value="`type-${type.value}`"
          >
            {{ type.title }}
          </v-tab>
          <v-tab
            v-for="item in shopCategories"
            :key="item.assignId"
            :value="`category-${item.assignId}`"
          >
            {{ item.categoryName }}
          </v-tab>
        </v-tabs>
        <div v-if="loading" class="py-4">Loading catalog...</div>
        <div v-else-if="matches.length" class="shop-list mb-4">
          <v-card
            v-for="item in matches"
            :key="item.assignId"
            class="shop-item"
            :class="{ 'shop-item-out-of-stock': isOutOfStock(item) }"
            variant="outlined"
          >
            <button
              type="button"
              :aria-label="title(item)"
              :aria-pressed="selected?.assignId === item.assignId"
              :disabled="disabled"
              class="shop-item-purchase text-left"
              :class="{ 'bg-primary': selected?.assignId === item.assignId }"
              @click="selectItem(item)"
            >
              <div class="shop-item-image d-flex justify-center align-center">
                <v-img
                  v-if="item.itemId.imageUrl"
                  :src="item.itemId.imageUrl"
                  height="100"
                />
                <v-icon v-else icon="mdi-image" size="48" />
              </div>
              <v-card-text class="pa-2">
                <div class="font-weight-medium">
                  {{ item.itemId.itemName ?? `Item #${item.itemId.assignId}` }}
                </div>
                <div v-if="isOutOfStock(item)" class="text-caption text-error">
                  Out of stock
                </div>
                <div class="text-caption">{{ item.itemId.itemSku }}</div>
                <div>{{ money(item.priceCents) }}</div>
              </v-card-text>
            </button>
          </v-card>
        </div>
        <v-alert v-else type="info" class="mb-4">No results</v-alert>
      </div>
    </div>
    <v-dialog v-model="configureOpen" max-width="640" scrollable>
      <v-card v-if="selected" :title="title(selected)">
        <v-form @submit.prevent="submit">
          <v-card-text>
            <div
              v-if="
                !restockOnly &&
                (!editing || selected.itemId.itemType !== ItemType.PRODUCT)
              "
              class="mb-3"
            >
              <v-chip :disabled="disabled" @click="recipientDialog = true">
                Shopping for: {{ dialogRecipientName }}
              </v-chip>
            </div>
            <v-alert v-if="error" type="error" class="my-3">{{
              error
            }}</v-alert>
            <p v-if="selected.itemId.itemDescription" class="mb-3">
              {{ selected.itemId.itemDescription }}
            </p>
            <v-alert v-if="outsideSaleDates" type="info" class="mb-3">
              This item is outside its sale dates. Staff can still add it.
            </v-alert>
            <v-alert
              v-if="selected.itemId.itemType === ItemType.PAID_CLASS"
              type="info"
            >
              Enroll through the class page, then open the signup's order here.
            </v-alert>
            <template v-else>
              <v-text-field
                v-if="selected.itemId.itemType === ItemType.PRODUCT"
                v-model.number="quantity"
                label="Quantity"
                type="number"
                :min="selected.minQuantity ?? 1"
                :max="selected.maxQuantity ?? undefined"
                :hint="quantityHint"
                persistent-hint
                :disabled="disabled"
              />
              <CurrencyInput
                v-if="selected.allowPriceOverride === true"
                class="mb-4"
                v-model:amount-cents="specifiedCents"
                :required="selected.itemId.itemType === ItemType.GIFT_CARD"
                :label="
                  selected.itemId.itemType === ItemType.DONATION
                    ? 'Donation amount'
                    : 'Amount'
                "
                clearable
                :disabled="disabled"
              />
              <v-alert
                v-if="
                  requiresRecipient(selected.itemId.itemType) &&
                  !dialogRecipient
                "
                type="info"
                class="mb-3"
              >
                Choose a recipient before adding this purchase.
              </v-alert>
              <v-text-field
                v-if="selected.itemId.itemType === ItemType.CARD"
                v-model="cardNumber"
                label="Card number (optional)"
                :hint="
                  editing
                    ? 'Leave blank to keep the current card number.'
                    : 'Leave blank to assign the next card number.'
                "
                persistent-hint
                :disabled="disabled"
              />
              <p v-else-if="restockOnly" class="mb-3">
                Add funds to balance {{ balanceNumber }}.
              </p>
              <v-text-field
                v-if="
                  selected.itemId.itemType === ItemType.GIFT_CARD &&
                  !restockOnly
                "
                v-model="balanceNumber"
                label="Balance account number (optional)"
                hint="Reload an existing gift card or create one with this number. Leave blank to assign a new number."
                persistent-hint
                :disabled="disabled"
              />
              <template
                v-if="selected.itemId.itemType === ItemType.GUEST_PRIVS"
              >
                <v-select
                  v-model="membership"
                  :items="memberships"
                  :item-title="membershipTitle"
                  item-value="assignId"
                  return-object
                  label="Recipient's membership"
                  :loading="loadingMemberships"
                  :disabled="disabled || !dialogRecipient || loadingMemberships"
                />
                <v-alert v-if="membershipError" type="error">{{
                  membershipError
                }}</v-alert>
                <v-btn
                  v-if="membershipError"
                  :disabled="disabled || loadingMemberships"
                  @click="loadMemberships"
                  >Retry memberships</v-btn
                >
                <p
                  v-if="
                    dialogRecipient &&
                    !loadingMemberships &&
                    !membershipError &&
                    !memberships.length
                  "
                >
                  This person has no memberships. Add a membership before
                  purchasing guest privileges.
                </p>
              </template>
              <StaffDonationFields
                v-if="selected.itemId.itemType === ItemType.DONATION"
                :key="`${selected.assignId}-${editing?.lineId ?? 'new'}`"
                v-model="donation"
                :initial="editing?.donation"
                :disabled="disabled"
              />
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="disabled" @click="configureOpen = false"
              >Cancel</v-btn
            >
            <v-btn
              v-if="selected.itemId.itemType !== ItemType.PAID_CLASS"
              color="primary"
              type="submit"
              :disabled="disabled || loadingMemberships"
              >{{ editing ? "Update item" : "Add to cart" }}</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="recipientDialog" max-width="900">
      <v-card title="Choose recipient" class="pa-4">
        <GeneratedPersonSearch picker @person-selected="selectRecipient" />
        <v-card-actions
          ><v-btn @click="recipientDialog = false">Close</v-btn></v-card-actions
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import type { PersonStaffSearchResult } from "../../../cbi-generator/generated/dto/PersonStaffSearchResult";
import { useNotificationStore } from "@/stores/notification";
import {
  buildCatalogPurchase,
  canQuickAdd,
  requiresRecipient,
  type CatalogPurchaseItem,
} from "@/composables/shop/catalogPurchase";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import { useStockService } from "../../../cbi-generator/frontend/composables/useStockFrontendService";
import type { MembershipStaff } from "../../../cbi-generator/generated/dto/MembershipStaff";
import type { PersonRef } from "../../../cbi-generator/generated/dto/PersonRef";
import type { AddToCart } from "../../../cbi-generator/generated/dto/AddToCart";
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import type { CategoryStaff } from "../../../cbi-generator/generated/dto/CategoryStaff";
import type { DonationPurchaseDetails } from "../../../cbi-generator/generated/dto/DonationPurchaseDetails";
import StaffDonationFields from "./StaffDonationFields.vue";

const props = defineProps<{
  items: CatalogPurchaseItem[];
  itemCatalogNames?: ReadonlyMap<number, string>;
  itemCategoryIds?: ReadonlyMap<number, readonly number[]>;
  shopCategories?: CategoryStaff[];
  buyer: PersonRef | null;
  buyerName: string | null;
  /** Who items are for until staff choose someone else; defaults to the buyer. */
  defaultRecipient?: PersonRef | null;
  defaultRecipientName?: string | null;
  disabled: boolean;
  loading: boolean;
  itemType?: ItemType;
  membershipId?: number;
  initialBalanceNumber?: string;
  /** Reloading an existing balance: the configure dialog asks only for the amount. */
  restock?: boolean;
  /** Adds the purchase; resolving to false keeps the configure dialog open. */
  onAdd?: (request: AddToCart) => unknown;
}>();
const selected = shallowRef<CatalogPurchaseItem | null>(null);
/** What a cart line holds now, to reconfigure it in place. */
export interface CatalogLineEdit {
  lineId: number;
  recipient: { personId: number; name: string } | null;
  quantity: number;
  specifiedCents: number | null;
  cardNumber: string | null;
  balanceNumber: string | null;
  donation: DonationPurchaseDetails | null;
  membershipId: number | null;
}
const editing = shallowRef<CatalogLineEdit | null>(null);
const restockOnly = computed(
  () =>
    props.restock === true &&
    !editing.value &&
    !!props.initialBalanceNumber &&
    selected.value?.itemId.itemType === ItemType.GIFT_CARD,
);
const configureOpen = computed({
  get: () => selected.value != null,
  set: (open: boolean) => {
    if (!open) selected.value = null;
  },
});
const search = ref("");
const tab = ref("all");
const typeTabs = [
  { value: ItemType.GIFT_CARD, title: "Gift Cards" },
  { value: ItemType.DONATION, title: "Donations" },
  { value: ItemType.MEMBERSHIP, title: "Memberships" },
  { value: ItemType.CARD, title: "Cards" },
  { value: ItemType.DAMAGE_WAIVER, title: "Damage Waivers" },
  { value: ItemType.GUEST_PRIVS, title: "Guest Privileges" },
].filter((type) => !props.itemType || type.value === props.itemType);
const quantity = ref(1);
const specifiedCents = ref<number>();
const donation = shallowRef<DonationPurchaseDetails | null>(null);
const cardNumber = ref("");
const balanceNumber = ref("");
const recipient = shallowRef<{ personId: number; name: string } | null>(
  props.defaultRecipient
    ? {
        personId: props.defaultRecipient.personId,
        name:
          props.defaultRecipientName ??
          `Person #${props.defaultRecipient.personId}`,
      }
    : null,
);
const recipientDialog = ref(false);
const memberships = shallowRef<MembershipStaff[]>([]);
const membership = shallowRef<MembershipStaff | null>(null);
const loadingMemberships = ref(false);
const membershipError = ref("");
const error = ref("");
const notification = useNotificationStore();
const stockAmounts = shallowRef<ReadonlyMap<number, number>>(new Map());
let stockVersion = 0;
watch(
  () => props.items,
  async (items) => {
    const version = ++stockVersion;
    stockAmounts.value = new Map();
    if (
      !items.some(
        (item) =>
          item.itemId.itemType === ItemType.PRODUCT &&
          item.ignoreStock !== true,
      )
    )
      return;
    try {
      const summary = await useStockService().stockSummaryStaff();
      if (version !== stockVersion) return;
      stockAmounts.value = new Map(
        summary.map((item) => [
          item.itemInstance.assignId,
          item.stockAmountRemaining,
        ]),
      );
    } catch (failure) {
      if (version !== stockVersion) return;
      notification.addSnackbar(
        failure instanceof Error
          ? failure.message
          : "Unable to load stock availability.",
        "error",
      );
    }
  },
  { immediate: true },
);
function isOutOfStock(item: CatalogPurchaseItem): boolean {
  const remaining = stockAmounts.value.get(item.assignId);
  return (
    item.itemId.itemType === ItemType.PRODUCT &&
    item.ignoreStock !== true &&
    remaining != null &&
    remaining <= 0
  );
}
let membershipVersion = 0;
onBeforeUnmount(() => {
  membershipVersion++;
  stockVersion++;
});

const money = (cents: number | null) =>
  cents == null
    ? "No fixed price"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
const title = (item: CatalogPurchaseItem) =>
  `${item.itemId.itemName ?? `Item #${item.itemId.assignId}`} - ${money(item.priceCents)} (#${item.assignId})`;
const eligibleItems = computed(() =>
  props.items.filter(
    (item) =>
      item.itemId.itemType !== ItemType.PAID_CLASS &&
      (!props.itemType || item.itemId.itemType === props.itemType),
  ),
);
const matches = computed(() => {
  const term = search.value?.trim().toLowerCase() ?? "";
  return eligibleItems.value.filter(
    (item) =>
      (tab.value === "all" ||
        tab.value === `type-${item.itemId.itemType}` ||
        (tab.value.startsWith("category-") &&
          props.itemCategoryIds
            ?.get(item.itemId.assignId)
            ?.includes(Number(tab.value.slice(9))))) &&
      (!term ||
        `${title(item)} ${item.itemId.itemSku ?? ""} ${item.itemId.itemDescription ?? ""} ${item.itemId.itemType ?? ""}`
          .toLowerCase()
          .includes(term)),
  );
});
watch(matches, (items) => {
  if (
    selected.value &&
    !editing.value &&
    !items.some((item) => item.assignId === selected.value?.assignId)
  )
    selected.value = null;
});
const effectiveRecipient = computed<PersonRef | null>(() =>
  recipient.value ? { personId: recipient.value.personId } : props.buyer,
);
/** Who the configure dialog is for: the edited line's recipient, or the shopping recipient. */
const dialogRecipient = computed<PersonRef | null>(() =>
  editing.value
    ? editing.value.recipient
      ? { personId: editing.value.recipient.personId }
      : null
    : effectiveRecipient.value,
);
const recipientName = computed(() => {
  const name =
    recipient.value?.name ??
    props.buyerName ??
    (props.buyer ? `Person #${props.buyer.personId}` : "Anonymous buyer");
  return props.buyer &&
    effectiveRecipient.value?.personId === props.buyer.personId
    ? `${name} (buyer)`
    : name;
});
const dialogRecipientName = computed(() => {
  if (!editing.value) return recipientName.value;
  const person = editing.value.recipient;
  if (!person) return "No recipient";
  return props.buyer && person.personId === props.buyer.personId
    ? `${person.name} (buyer)`
    : person.name;
});
const outsideSaleDates = computed(
  () =>
    selected.value != null &&
    ((selected.value.startActive != null &&
      selected.value.startActive.getTime() > Date.now()) ||
      (selected.value.endActive != null &&
        selected.value.endActive.getTime() <= Date.now())),
);
const quantityHint = computed(
  () =>
    `Minimum ${selected.value?.minQuantity ?? 1}${
      selected.value?.maxQuantity != null
        ? `; maximum ${selected.value.maxQuantity}`
        : ""
    }.${editing.value ? "" : " Existing stock cart items are incremented."}`,
);
const membershipTitle = (item: MembershipStaff) =>
  `${item.membershipTypeId?.membershipTypeName ?? "Membership"} #${item.assignId} - ${item.purchasableState}${
    item.expirationDate
      ? ` (expires ${item.expirationDate.toLocaleDateString()})`
      : ""
  }`;

function selectRecipient(person: PersonStaffSearchResult) {
  if (!Number.isSafeInteger(person.personId) || person.personId <= 0) {
    notification.addSnackbar("Select a valid person.", "error");
    return;
  }
  const chosen = {
    personId: person.personId,
    name: person.name || `Person #${person.personId}`,
  };
  if (editing.value) editing.value = { ...editing.value, recipient: chosen };
  else recipient.value = chosen;
  recipientDialog.value = false;
}

async function loadMemberships() {
  const version = ++membershipVersion;
  memberships.value = [];
  membership.value = null;
  membershipError.value = "";
  loadingMemberships.value = false;
  const person = dialogRecipient.value;
  if (!person || selected.value?.itemId.itemType !== ItemType.GUEST_PRIVS)
    return;
  loadingMemberships.value = true;
  try {
    const result = await usePersonService().getPersonStaffMemberships(
      person.personId,
      { personId: false, membershipTypeId: true },
    );
    if (version !== membershipVersion) return;
    memberships.value = result;
    membership.value =
      result.find(
        (item) =>
          item.assignId === (editing.value?.membershipId ?? props.membershipId),
      ) ?? null;
  } catch (failure) {
    if (version !== membershipVersion) return;
    membershipError.value =
      failure instanceof Error
        ? failure.message
        : "Unable to load memberships.";
    notification.addSnackbar(membershipError.value, "error");
  } finally {
    if (version === membershipVersion) loadingMemberships.value = false;
  }
}
watch(
  () => [dialogRecipient.value?.personId, selected.value?.assignId],
  loadMemberships,
);
watch(
  () => selected.value?.assignId,
  () => {
    const edit = selected.value ? editing.value : null;
    if (!selected.value) editing.value = null;
    quantity.value =
      edit?.quantity ?? Math.max(1, selected.value?.minQuantity ?? 1);
    specifiedCents.value = edit?.specifiedCents ?? undefined;
    donation.value = null;
    cardNumber.value = edit?.cardNumber ?? "";
    balanceNumber.value = edit
      ? (edit.balanceNumber ?? "")
      : (props.initialBalanceNumber ?? "");
    error.value = "";
  },
);

async function submit() {
  const item = selected.value;
  if (props.disabled || loadingMemberships.value || !item) return;
  error.value = "";
  const edit = editing.value;
  let request: AddToCart;
  try {
    request = buildCatalogPurchase(item, {
      recipient:
        edit && item.itemId.itemType === ItemType.PRODUCT
          ? null
          : dialogRecipient.value,
      quantity: quantity.value,
      specifiedCents: specifiedCents.value,
      cardNumber: cardNumber.value,
      balanceNumber: balanceNumber.value,
      membership: membership.value
        ? { assignId: membership.value.assignId }
        : null,
      donation: donation.value,
      updateLine: edit ? { assignId: edit.lineId } : null,
    });
  } catch (failure) {
    error.value =
      failure instanceof Error ? failure.message : "Invalid purchase.";
    notification.addSnackbar(error.value, "error");
    return;
  }
  // Keep the dialog (and what staff entered) open when the cart rejects the purchase.
  if ((await props.onAdd?.(request)) !== false && selected.value === item)
    selected.value = null;
}

function configureItem(item: CatalogPurchaseItem) {
  if (props.disabled) return;
  editing.value = null;
  selected.value = item;
}

/** Opens the configure dialog for an existing cart line of [item]; submitting updates that line. */
function configureLine(item: CatalogPurchaseItem, edit: CatalogLineEdit) {
  if (props.disabled) return;
  editing.value = edit;
  selected.value = item;
}
defineExpose({ configureLine });

function selectItem(item: CatalogPurchaseItem) {
  if (props.disabled) return;
  if (!canQuickAdd(item, effectiveRecipient.value)) {
    configureItem(item);
    return;
  }
  selected.value = null;
  error.value = "";
  try {
    void props.onAdd?.(
      buildCatalogPurchase(item, {
        recipient: effectiveRecipient.value,
        quantity: Math.max(1, item.minQuantity ?? 1),
        specifiedCents: undefined,
        cardNumber: "",
        balanceNumber: props.initialBalanceNumber ?? "",
        membership: null,
      }),
    );
  } catch (failure) {
    error.value =
      failure instanceof Error ? failure.message : "Invalid purchase.";
    notification.addSnackbar(error.value, "error");
  }
}

let autoSelected = false;
watch(
  [eligibleItems, () => props.loading, () => props.disabled],
  ([items, loading, disabled]) => {
    if (
      autoSelected ||
      !props.itemType ||
      loading ||
      disabled ||
      items.length !== 1
    )
      return;
    autoSelected = true;
    selectItem(items[0]);
  },
  { immediate: true },
);
</script>

<style scoped>
.shop-tool-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: clamp(12rem, 55dvh, 48rem);
}

.catalog-purchase {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  height: clamp(16rem, 60dvh, 48rem);
}

.shop-tool-panel > div {
  height: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.shop-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(12rem, 100%), 1fr));
  gap: 1rem;
}

.shop-item {
  position: relative;
  min-width: 0;
}

.shop-item-out-of-stock {
  background-color: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface-variant));
}

.shop-item-out-of-stock .shop-item-image {
  filter: grayscale(1);
  opacity: 0.45;
}

.shop-item-purchase {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shop-item-purchase .font-weight-medium {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.shop-item-image {
  flex: 0 0 100px;
  width: 100%;
  height: 100px;
  background-color: rgb(var(--v-theme-surface));
}
</style>
